import * as enabledApi from '@shared/api/enabledPlayApi';
import AssociatedProfileModel from '@shared/models/deviceProfiles/AssociatedProfileModel';
import DeviceProfileShareModel from '@shared/models/deviceProfiles/DeviceProfileShareModel';
import GenerateProfileRequest from '@shared/models/deviceProfiles/GenerateProfileRequest';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { OSTypes } from '@shared/models/devices/OSTypes';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import dayjs, { Dayjs } from 'dayjs';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createContainer } from 'unstated-next';
import { publish, subscribe } from '../helpers/EventCallback';
import useStateRef from '../useStateRef';

function useProfilesContainer() {
    const { t } = useTranslation();
    const [profiles, setProfiles, profilesRef] = useStateRef<UserDeviceProfileModel[]>([]);
    const [allProfiles, setAllProfiles, allProfilesRef] = useStateRef<UserDeviceProfileModel[]>([]);
    const [organizationProfiles, setOrganizationProfiles, organizationProfilesRef] = useStateRef<
        UserDeviceProfileModel[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const lastModifiedDate = useRef<Date | null>(null);
    const associatedProgramsListRef = useRef<{
        [appName: string]: AssociatedProfileModel[];
    }>({});
    const associatedProgramsAppNames = useRef<string[]>([]);
    const appNameFuzzySearchRef = useRef<FuzzySearch<string> | null>(null);

    const getSortedProfiles = (newProfiles: UserDeviceProfileModel[]) => {
        const sortedProfiles = newProfiles.slice().sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
        return sortedProfiles;
    };

    const setLocalProfiles = (newProfiles: UserDeviceProfileModel[], ignoreLocalStorage: boolean = false) => {
        const sortedProfiles = getSortedProfiles(newProfiles);
        setProfiles(sortedProfiles);
        if (!ignoreLocalStorage) {
            localStorage.setItem('DEVICE_PROFILES', JSON.stringify(sortedProfiles));
        }
    };

    const initializeFromStorage = () => {
        try {
            const localProfiles = localStorage.getItem('DEVICE_PROFILES');
            if (localProfiles) {
                setLocalProfiles(JSON.parse(localProfiles), true);
            }
        } catch (e) {
            console.log(e);
        }
    };
    const getProfiles = async (): Promise<IResult<UserDeviceProfileModel[]>> => {
        try {
            setIsLoading(true);
            const result = await enabledApi.getDeviceProfiles();
            setIsLoading(false);
            if (result?.resultType == 'Ok' && result.data) {
                setLocalProfiles(result.data);
            }
            return result;
        } catch (e) {
            setIsLoading(false);
            console.log(e);
            setErrors(['Error getting profiles']);
            return new UnexpectedResult<UserDeviceProfileModel[]>();
        }
    };
    const deleteProfile = async (profileId: string, isHidden?: boolean) => {
        try {
            const result = await enabledApi.deleteProfile(profileId);
            if (result?.resultType == 'Ok') {
                const clone = _.cloneDeep(profilesRef.current);
                const newProfiles = clone.filter((x) => x.id != profileId);
                const profile = profilesRef.current.find((x) => x.id == profileId);
                publish('profileDeleted', { profile });
                setLocalProfiles(newProfiles);
                if (!isHidden) {
                    publish('alertSuccess', {
                        message: t('common:profile.success.delete'),
                        timeout: 5000,
                    });
                }
            }
            return result;
        } catch (e) {
            console.log(e);
            setErrors(['Error deleting profile']);
            return new UnexpectedResult<UserDeviceProfileModel[]>();
        }
    };
    const addProfile = async (profile: UserDeviceProfileModel) => {
        const newProfiles = _.cloneDeep(profilesRef.current);
        if (newProfiles.find((x) => x.id == profile.id)) {
            updateProfile(profile);
        } else {
            newProfiles.push(profile);
            setLocalProfiles(newProfiles);
        }
    };
    const updateProfile = (profile: UserDeviceProfileModel) => {
        const clone = _.cloneDeep(profilesRef.current);
        const existingProfile = clone.find((x) => x.id === profile.id);
        if (existingProfile && _.isEqual(existingProfile, profile)) {
            return;
        }
        const newProfiles = clone.map((x) => {
            if (x.id == profile.id) {
                return profile;
            }
            return x;
        });
        setLocalProfiles(newProfiles);
    };

    const removeLocalProfile = (profileId: string) => {
        const clone = _.cloneDeep(profilesRef.current);
        const newProfiles = clone.filter((x) => x.id != profileId);
        setLocalProfiles(newProfiles);
    };
    const createShareLink = async (
        profileId: string | undefined,
        isSingleUse: boolean,
        expirationDate: Dayjs | null
    ) => {
        if (!profileId) return new UnexpectedResult<DeviceProfileShareModel>();
        try {
            const result = await enabledApi.shareProfile(profileId, {
                expirationDate: expirationDate?.toISOString(),
                isSingleUse,
                isPublic: true,
            });
            if (result?.resultType == 'Ok') {
                publish('alertSuccess', {
                    message: t('common:profile.success.share'),
                    timeout: 5000,
                });
            }
            return result;
        } catch (e) {
            console.log(e);
            setErrors(['Error creating share link']);
            return new UnexpectedResult<DeviceProfileShareModel>();
        }
    };
    const getShares = async (profileId: string | undefined) => {
        if (!profileId) return new UnexpectedResult<DeviceProfileShareModel[]>();
        try {
            const result = await enabledApi.getProfileShares(profileId);
            return result;
        } catch (e) {
            setErrors(['Error getting shares']);
            return new UnexpectedResult<DeviceProfileShareModel[]>();
        }
    };
    const deleteShare = async (shareId: string) => {
        try {
            const result = await enabledApi.disableProfileShare(shareId);
            if (result?.resultType == 'Ok') {
                publish('alertSuccess', {
                    message: t('common:profile.success.shareDelete'),
                    timeout: 5000,
                });
            }
            return result;
        } catch (e) {
            console.log(e);
            setErrors(['Error deleting share link']);
            return new UnexpectedResult<DeviceProfileShareModel>();
        }
    };
    const generateProfile = async (request: GenerateProfileRequest) => {
        try {
            const result = await enabledApi.generateUserDeviceProfile(request);
            if (result?.resultType == 'Ok') {
                publish('alertSuccess', {
                    message: t('common:profile.success.generate'),
                    timeout: 5000,
                });
            }
            return result;
        } catch (e) {
            console.log(e);
            setErrors(['Error generating profile']);
            return new UnexpectedResult<UserDeviceProfileModel>();
        }
    };

    const clearProfiles = () => {
        setProfiles([]);
        setAllProfiles([]);
        localStorage.removeItem('DEVICE_PROFILES');
    };

    const getAssociationsFromProfiles = (localProfiles: UserDeviceProfileModel[]) => {
        // {appName: AssociatedProfileModel[]} -> sparse storage
        const associations: { [appName: string]: AssociatedProfileModel[] } = {};

        /** sort local profiles by descending order of modified date */
        const sortedProfiles = _.cloneDeep(localProfiles);
        sortedProfiles.sort((a, b) => {
            return dayjs(a.modifiedDate) > dayjs(b.modifiedDate) ? -1 : 1;
        });

        sortedProfiles.forEach((profile) => {
            profile.configuration.associatedPrograms?.forEach((appName) => {
                const cleansedAppName = appName.trim().toLowerCase();
                if (cleansedAppName === '') {
                    return;
                }

                if (!(cleansedAppName in associations)) {
                    associations[cleansedAppName] = [];
                }
                if (profile.id) {
                    associations[cleansedAppName].push({
                        id: profile.id,
                        name: profile.name,
                        isPublic: profile.isPublic,
                        platform: profile.platform,
                    });
                }
            });
        });
        return associations;
    };

    const updateCombinedProfiles = (localProfiles: UserDeviceProfileModel[]) => {
        const hasNewModifications = () => {
            if (allProfilesRef.current.length === 0) {
                return true;
            }
            if (allProfilesRef.current.length !== localProfiles.length) {
                return true;
            }

            // Check if any profile content has actually changed
            // DONT use last modified date for comparison as it can be unreliable
            return localProfiles.some((localProfile) => {
                const existingProfile = allProfilesRef.current.find((p) => p.id === localProfile.id);
                if (!existingProfile) return true;
                return (
                    !_.isEqual(existingProfile.configuration, localProfile.configuration) ||
                    existingProfile.name !== localProfile.name ||
                    existingProfile.description !== localProfile.description
                );
            });
        };

        if (hasNewModifications()) {
            lastModifiedDate.current = new Date();
            // set available profiles
            setAllProfiles(localProfiles);

            // calculate associated programs
            const associations = getAssociationsFromProfiles(localProfiles);
            associatedProgramsListRef.current = associations;

            const appNames = Object.keys(associations);
            associatedProgramsAppNames.current = appNames;
            appNameFuzzySearchRef.current = new FuzzySearch(appNames, [], {
                sort: true,
                caseSensitive: false,
            });
        }
    };

    useEffect(() => {
        // custom profiles updated
        const publicProfiles = allProfilesRef.current.filter((profile) => profile.isPublic);
        const combinedProfiles = [...profilesRef.current, ...publicProfiles];
        updateCombinedProfiles(combinedProfiles);
    }, [profiles]);

    const fuzzySearchProfiles = (search: string) => {
        const searcher = new FuzzySearch(
            allProfilesRef.current,
            ['name', 'description', 'configuration.associatedPrograms'],
            {
                sort: true,
                caseSensitive: false,
            }
        );
        return searcher.search(search);
    };

    const getIndicatorName = (name: string, isPublic?: boolean) => {
        return !isPublic ? t('common:profile.custom', { name }) : name;
    };

    const isPlatformCompatible = (devicePlatform: string | undefined, profilePlatform: string = 'generic'): boolean => {
        const normalizedDevicePlatform = devicePlatform?.toLowerCase();

        if (!devicePlatform || normalizedDevicePlatform == 'generic') return true;

        const normalizedProfilePlatform = profilePlatform.toLowerCase();

        return normalizedDevicePlatform === normalizedProfilePlatform || normalizedProfilePlatform === 'generic';
    };

    const filterProfiles = (
        search: string,
        platform: string = 'generic',
        userVoiceLocale: string,
        selectedInterest?: string
    ) => {
        const normalizedFilter = search.toLowerCase().trim();
        const normalizedInterest = selectedInterest?.toLowerCase().trim();

        const filtered = allProfilesRef.current.filter((profile) => {
            if (!profile || !isPlatformCompatible(platform, profile.platform)) return false;

            // Filter by profile group and locale (from mobile)
            if (
                profile.deviceProfileGroupId &&
                profile.defaultLocale !== userVoiceLocale &&
                !profile.isDefaultInGroup
            ) {
                return false;
            }

            // Show default profile only if user's locale doesn't have a match
            if (profile.deviceProfileGroupId && profile.isDefaultInGroup && profile.defaultLocale !== userVoiceLocale) {
                const hasCurrentLocale = profile.deviceProfileGroup?.some(
                    (groupMember) => groupMember.locale === userVoiceLocale
                );
                if (hasCurrentLocale) return false;
            }

            // Gather searchable fields from profile and originated profile
            const fields = [
                getIndicatorName(profile.name, profile.isPublic).toLowerCase(),
                profile.description?.toLowerCase() || '',
                ...(profile.configuration.associatedPrograms?.map((p) => p.toLowerCase()) || []),
            ];

            const originated = allProfilesRef.current.find((p) => p.id === profile.originatedByProfileId);
            if (originated) {
                fields.push(
                    getIndicatorName(originated.name, originated.isPublic).toLowerCase(),
                    originated.description?.toLowerCase() || '',
                    ...(originated.configuration.associatedPrograms?.map((p) => p.toLowerCase()) || [])
                );
            }

            const matchesFilter = fields.some((field) => field.includes(normalizedFilter));
            const matchesInterest =
                !normalizedInterest || profile.interests.map((i) => i.toLowerCase()).includes(normalizedInterest);

            return matchesFilter && matchesInterest;
        });

        return filtered;
    };

    const isPlatformSupportedProfile = (profile: UserDeviceProfileModel, os: OSTypes) => {
        if (!profile) {
            return false;
        }

        return !profile.platform || profile.platform === os || profile.platform === 'generic';
    };

    const getAssociatedAppName = (appName: string): string | null => {
        const cleansedAppName = appName.trim().toLowerCase();
        if (!cleansedAppName) return null;

        // Try fuzzy search first
        const fuzzyResults = appNameFuzzySearchRef.current?.search(cleansedAppName);
        if (fuzzyResults?.length) {
            return fuzzyResults[0];
        }

        // Try exact match
        const storedNames = associatedProgramsAppNames.current;
        const exactMatch = storedNames.find((name) => name.toLowerCase() === cleansedAppName);
        if (exactMatch) return exactMatch;

        // Calculate scores for partial matches
        const scores = calculateMatchScores(cleansedAppName, storedNames);
        const bestMatch = getBestMatch(scores);

        return bestMatch;
    };

    const calculateMatchScores = (searchTerm: string, names: string[]): Map<string, number> => {
        const searchParts = searchTerm.split(/[\s-]+/);
        const scoreMap = new Map<string, number>();

        for (const name of names) {
            const cleansedName = name.toLowerCase();
            const nameParts = cleansedName.split(/[\s-]+/);
            let score = 0;

            // Check for sequential phrase matches
            const isSequentialMatch =
                searchParts.join(' ').includes(nameParts.join(' ')) ||
                nameParts.join(' ').includes(searchParts.join(' '));
            if (isSequentialMatch) score += 10;

            // Discrete partial word matches
            const matchCount = Math.max(
                searchParts.filter((part) => nameParts.includes(part)).length,
                nameParts.filter((part) => searchParts.includes(part)).length
            );
            score += matchCount;

            // Check for substring matches
            if (cleansedName.includes(searchTerm) || searchTerm.includes(cleansedName)) {
                score += 1;
            }

            if (score > 0) scoreMap.set(name, score);
        }

        return scoreMap;
    };

    const getBestMatch = (scores: Map<string, number>): string | null => {
        if (scores.size === 0) return null;

        let bestScore = 0;
        let bestMatch = null;

        for (const [name, score] of scores) {
            if (score > bestScore) {
                bestScore = score;
                bestMatch = name;
            }
        }

        return bestMatch;
    };

    useEffect(() => {
        // public available profiles updated
        const unsubscribeDefaultProfiles = subscribe('default-profiles-updated', (data: UserDeviceProfileModel[]) => {
            updateCombinedProfiles([...profilesRef.current, ...data]);
        });

        const unsubscribeUserProfileAdded = subscribe(
            'userProfileAdded',
            (data: { profile: UserDeviceProfileModel }) => {
                addProfile(data.profile);
            }
        );

        const unsubscribeUserProfileUpdated = subscribe(
            'userProfileUpdated',
            (data: { profile: UserDeviceProfileModel }) => {
                updateProfile(data.profile);
            }
        );

        const unsubscribeUserProfileDeleted = subscribe('userProfileDeleted', (data: { profileId: string }) => {
            deleteProfile(data.profileId, true);
        });

        return () => {
            unsubscribeDefaultProfiles();
            unsubscribeUserProfileAdded();
            unsubscribeUserProfileUpdated();
            unsubscribeUserProfileDeleted();
        };
    }, []);

    return {
        profiles,
        profilesRef,
        clearProfiles,
        addProfile,
        updateProfile,
        getProfiles,
        deleteProfile,
        createShareLink,
        getShares,
        deleteShare,
        generateProfile,
        isLoading,
        errors,
        initializeFromStorage,
        removeLocalProfile,
        allProfiles,
        allProfilesRef,
        associatedProgramsListRef,
        fuzzySearchProfiles,
        filterProfiles,
        getIndicatorName,
        isPlatformSupportedProfile,
        getAssociatedAppName,
        organizationProfiles,
        setOrganizationProfiles,
        organizationProfilesRef,
    };
}

const ProfilesContainer = createContainer(useProfilesContainer);
export default ProfilesContainer;
